import { SupportContact } from "../components/SupportContact";
import { useState } from "react";
import { ChefHat, Mail, Lock, ArrowRight, KeyRound, UserPlus, LogIn, Loader2, Smartphone } from "lucide-react";
import { api, errorMessage } from "../api";
import { useAuth } from "./AuthContext";

const TABS = [
  { id: "signin", label: "Sign in", icon: LogIn },
  { id: "otp", label: "Email OTP", icon: KeyRound },
  { id: "signup", label: "Create account", icon: UserPlus },
  { id: "staff", label: "Staff login", icon: Smartphone },
];

export default function AuthScreens() {
  const [tab, setTab] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);
  const [phone, setPhone] = useState("");
  const [mockOtp, setMockOtp] = useState("");
  const [newPw, setNewPw] = useState("");
  const { setAuth } = useAuth();

  const run = async (fn) => {
    setBusy(true); setError(""); setInfo("");
    try { await fn(); }
    catch (e) { setError(errorMessage(e)); }
    finally { setBusy(false); }
  };

  const signin = (e) => { e.preventDefault(); run(async () => {
    const { data } = await api.post("/auth/login", { email, password });
    setAuth(data);
  });};

  const resetRequest = (e) => { e.preventDefault(); run(async () => {
    await api.post("/auth/request-otp", { email });
    setInfo("We emailed you a 6-digit code. Enter it with your new password."); setOtpSent(true);
  });};

  const resetSubmit = (e) => { e.preventDefault(); run(async () => {
    const { data } = await api.post("/auth/reset-password", { email, otp, new_password: newPw });
    setTab("signin"); setPassword(""); setOtp(""); setOtpSent(false); setNewPw(""); setInfo(data.message);
  });};

  const signup = (e) => { e.preventDefault(); run(async () => {
    const { data } = await api.post("/auth/register", { email, password, name });
    setAuth(data);
  });};

  const requestOtp = (e) => { e.preventDefault(); run(async () => {
    const { data } = await api.post("/auth/request-otp", { email });
    setInfo(data.message || "A 6-digit code was sent to your email.");
    setOtpSent(true);
  });};

  const verifyOtp = (e) => { e.preventDefault(); run(async () => {
    const { data } = await api.post("/auth/verify-otp", { email, otp });
    setAuth(data);
  });};

  const staffRequest = (e) => { e.preventDefault(); run(async () => {
    const { data } = await api.post("/auth/staff/request-otp", { phone });
    setInfo(data.message); setMockOtp(data.mock_otp || ""); setOtpSent(true);
  });};

  const staffVerify = (e) => { e.preventDefault(); run(async () => {
    const { data } = await api.post("/auth/staff/verify-otp", { phone, otp });
    setAuth(data);
  });};

  return (
    <div className="auth-shell" data-testid="auth-shell">
      <section className="auth-brand" aria-hidden>
        <div className="auth-mark"><ChefHat size={22} /></div>
        <p className="auth-overline">FYNDINE · RESTAURANT OS</p>
        <h1>Run every kitchen like a flagship.</h1>
        <p className="auth-lede">Inventory, recipe costing, GST-ready invoicing and multi-branch intelligence — built for India's restaurants by VSM Transnational Pvt Ltd.</p>
        <ul className="auth-points">
          <li><span>14-day free trial</span> on every new account</li>
          <li><span>₹, Lakh/Crore, IST</span> localisation across the board</li>
          <li><span>GST-compliant bills</span> delivered on WhatsApp or SMS</li>
        </ul>
      </section>

      <section className="auth-card panel">
        <div className="auth-tabs">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button key={id} className={tab === id ? "auth-tab active" : "auth-tab"}
              onClick={() => { setTab(id); setError(""); setInfo(""); setOtpSent(false); setOtp(""); setMockOtp(""); }}
              data-testid={`auth-tab-${id}`} type="button">
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>

        {tab === "signin" && (
          <form onSubmit={signin} className="auth-form">
            <h2>Welcome back</h2>
            <p className="auth-sub">Sign in to continue to your kitchen workspace.</p>
            <label><Mail size={14}/>Email<input type="email" required value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@restaurant.in" data-testid="signin-email"/></label>
            <label><Lock size={14}/>Password<input type="password" required value={password} onChange={e=>setPassword(e.target.value)} placeholder="Your password" data-testid="signin-password"/></label>
            <button className="auth-primary" disabled={busy} data-testid="signin-submit">{busy ? <Loader2 className="spin" size={16}/> : <ArrowRight size={16}/>} Sign in</button>
            <button type="button" className="auth-link" onClick={() => { setTab("reset"); setError(""); setInfo(""); setOtpSent(false); setOtp(""); }} data-testid="forgot-password">{/locked/i.test(error) ? "Unlock — reset your password" : "Forgot password?"}</button>
          </form>
        )}

        {tab === "reset" && (
          <form onSubmit={otpSent ? resetSubmit : resetRequest} className="auth-form" data-testid="reset-form">
            <h2>Reset password</h2>
            <p className="auth-sub">Resetting also unlocks an account locked after wrong passwords.</p>
            <label><Mail size={14}/>Email<input type="email" required value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@restaurant.in" data-testid="reset-email"/></label>
            {otpSent && <>
              <label>Email code<input required inputMode="numeric" maxLength={6} value={otp} onChange={e=>setOtp(e.target.value.replace(/\D/g, ""))} placeholder="6-digit code" data-testid="reset-otp"/></label>
              <label><Lock size={14}/>New password<input type="password" required minLength={8} value={newPw} onChange={e=>setNewPw(e.target.value)} placeholder="At least 8 characters" data-testid="reset-new-password"/></label>
            </>}
            <button className="auth-primary" disabled={busy} data-testid="reset-submit">{busy ? <Loader2 className="spin" size={16}/> : <ArrowRight size={16}/>} {otpSent ? "Set new password" : "Email me a code"}</button>
            <button type="button" className="auth-link" onClick={() => { setTab("signin"); setError(""); }} data-testid="reset-back">Back to sign in</button>
          </form>
        )}

        {tab === "signup" && (
          <form onSubmit={signup} className="auth-form">
            <h2>Create your account</h2>
            <p className="auth-sub">14-day free trial begins instantly. No card required.</p>
            <label>Full name<input required value={name} onChange={e=>setName(e.target.value)} placeholder="Owner name" data-testid="signup-name"/></label>
            <label><Mail size={14}/>Email<input type="email" required value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@restaurant.in" data-testid="signup-email"/></label>
            <label><Lock size={14}/>Password<input type="password" required minLength={8} value={password} onChange={e=>setPassword(e.target.value)} placeholder="At least 8 characters" data-testid="signup-password"/></label>
            <button className="auth-primary" disabled={busy} data-testid="signup-submit">{busy ? <Loader2 className="spin" size={16}/> : <ArrowRight size={16}/>} Start free trial</button>
          </form>
        )}

        {tab === "otp" && (
          <form onSubmit={otpSent ? verifyOtp : requestOtp} className="auth-form">
            <h2>Sign in with Email OTP</h2>
            <p className="auth-sub">We'll email you a 6-digit code. No password needed.</p>
            <label><Mail size={14}/>Email<input type="email" required value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@restaurant.in" data-testid="otp-email" disabled={otpSent}/></label>
            {otpSent && (
              <label><KeyRound size={14}/>6-digit code<input required value={otp} onChange={e=>setOtp(e.target.value.replace(/\D/g,"").slice(0,6))} inputMode="numeric" maxLength={6} placeholder="123456" data-testid="otp-code" autoFocus/></label>
            )}
            <button className="auth-primary" disabled={busy} data-testid={otpSent ? "otp-verify" : "otp-request"}>
              {busy ? <Loader2 className="spin" size={16}/> : <ArrowRight size={16}/>}
              {otpSent ? "Verify & sign in" : "Email me a code"}
            </button>
            {otpSent && (
              <button type="button" className="auth-link" onClick={()=>{setOtpSent(false);setOtp("");}} data-testid="otp-reset">Use a different email</button>
            )}
          </form>
        )}

        {tab === "staff" && (
          <form onSubmit={otpSent ? staffVerify : staffRequest} className="auth-form">
            <h2>Staff sign in</h2>
            <p className="auth-sub">Managers, cashiers, waiters and kitchen crew — sign in with the mobile number your owner registered.</p>
            <label><Smartphone size={14}/>Mobile number<input required inputMode="numeric" value={phone} onChange={e=>setPhone(e.target.value.replace(/[^\d+ ]/g,""))} placeholder="Enter 10-digit mobile number" data-testid="staff-phone" disabled={otpSent}/></label>
            {otpSent && (
              <label><KeyRound size={14}/>6-digit OTP<input required value={otp} onChange={e=>setOtp(e.target.value.replace(/\D/g,"").slice(0,6))} inputMode="numeric" maxLength={6} placeholder="123456" data-testid="staff-otp" autoFocus/></label>
            )}
            <button className="auth-primary" disabled={busy} data-testid={otpSent ? "staff-verify" : "staff-request-otp"}>
              {busy ? <Loader2 className="spin" size={16}/> : <ArrowRight size={16}/>}
              {otpSent ? "Verify & sign in" : "Send OTP"}
            </button>
            {otpSent && <button type="button" className="auth-link" onClick={()=>{setOtpSent(false);setOtp("");setMockOtp("");}} data-testid="staff-change-number">Use a different number</button>}
            {!otpSent && <button type="button" className="auth-link" onClick={()=>{ if (!phone.trim()) { setError("Enter your mobile number first"); return; } setError(""); setInfo("Enter the code your manager gave you."); setOtpSent(true); }} data-testid="staff-have-code">I have a code from my manager</button>}
          </form>
        )}

        {error && <div className="auth-alert error" data-testid="auth-error">{error}</div>}
        {info && !error && <div className="auth-alert info" data-testid="auth-info">{info}</div>}

        <div className="auth-foot">
          <small>By continuing you agree to our Terms &amp; Privacy. Secure. India-based. GST ready.</small>
          <small><a href="/download" data-testid="auth-get-apps-link" style={{ color: "#c2410c", fontWeight: 600 }}>Get FynDine for Android & Windows →</a></small>
          <SupportContact tid="auth-support" />
        </div>
      </section>
    </div>
  );
}
